import React, { useEffect, useMemo, useState } from "react";
import { Button, Table } from "antd";
import type { TableColumnsType, TableProps } from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import axios from "axios";
import ModalUser from "./ModalUser";
import { useConfirmModal } from "./ModalDelete";
import Swal from "sweetalert2";
import ModalAppCoin from "./ModalAppCoin";
import Input from "antd/es/input/Input";

type TableRowSelection<T extends object = object> =
  TableProps<T>["rowSelection"];

export interface DataType {
  _id: string;
  username: string;
  phone: string;
  role: string;
  coins: number;
}

const extractUserId = (user: Record<string, unknown>): string => {
  const rawId = user._id ?? user.id;
  if (!rawId) return "";
  if (typeof rawId === "string") return rawId;
  if (typeof rawId === "object" && rawId !== null) {
    const oid = (rawId as { $oid?: string }).$oid;
    if (oid) return oid;
    if (typeof (rawId as { toString?: () => string }).toString === "function") {
      const value = (rawId as { toString: () => string }).toString();
      if (value && value !== "[object Object]") {
        return value;
      }
    }
  }
  return String(rawId);
};

const normalizeUser = (user: Partial<DataType> & { id?: string }): DataType => ({
  _id: extractUserId(user as Record<string, unknown>),
  username: user.username || "",
  phone: user.phone || "",
  role: user.role || "",
  coins: Number(user.coins || 0),
});

const parseUsersResponse = (data: unknown): DataType[] => {
  const rawUsers = Array.isArray(data)
    ? data
    : Array.isArray((data as { data?: unknown[] })?.data)
      ? (data as { data: Partial<DataType>[] }).data
      : [];

  return rawUsers.map(normalizeUser);
};

const fetchAllUsers = async (token: string): Promise<DataType[]> => {
  const baseUrl = process.env.REACT_APP_URL_API;
  const headers = {
    Authorization: `Bearer ${token}`,
    accept: "*/*",
  };
  const endpoints = [`${baseUrl}/users`, `${baseUrl}/users/all`];

  let lastError: unknown = null;

  for (const endpoint of endpoints) {
    try {
      const response = await axios.get(endpoint, { headers });
      const users = parseUsersResponse(response.data);
      if (users.length > 0 || endpoint === endpoints[endpoints.length - 1]) {
        return users;
      }
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError;
};

const ListUser: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [dataUser, setDataUser] = useState<DataType[]>([]);
  const [isShowCreate, setIsShowCreate] = useState(false);
  const [isShowEdit, setIsShowEdit] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const Cookie = require("js-cookie");
  const token = Cookie.get("access_token");
  const [dataEdit, setDataEdit] = useState<DataType>();
  const [isShowAppCoin, setIsShowAppCoin] = useState(false);
  const [idUser, setIdUser] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const { showConfirm, contextHolder } = useConfirmModal();

  const handleDelete = (user: DataType) => {
    showConfirm({
      title: "Xoá người dùng",
      content: `Bạn có chắc muốn xoá người dùng ${user.username} không?`,
      onOk: async () => {
        await axios
          .delete(`${process.env.REACT_APP_URL_API}/users/${String(user._id)}`, {
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "*/*",
            },
          })
          .then((data) => {
            if (data.status === 200) {
              Swal.fire({
                icon: "success",
                title: "Xoá tài khoản thành công",
                text: "Vui lòng đợi ít phút. ",
                timer: 1000,
                timerProgressBar: true,
                customClass: {
                  popup: "custom-swal",
                  title: "custom-title",
                  icon: "custom-icon",
                },
              });
              setRefreshTrigger((prev) => prev + 1);
            }
          })
          .catch((err) => {
            return err;
          });
      },
    });
  };

  const columns: TableColumnsType<DataType> = [
    {
      title: "Name",
      dataIndex: "username",
      align: "center",
    },
    {
      title: "Role",
      dataIndex: "role",
      align: "center",
    },
    {
      title: "Phone",
      align: "center",
      render: (_, record: DataType) => record.phone || "****",
    },
    {
      title: "COINS",
      dataIndex: "coins",
      align: "center",
    },
    {
      title: "Action",
      align: "center",
      render: (_, record: DataType) => {
        return (
          <div className="flex gap-3 justify-center items-center">
            <Button
              icon={<PlusCircleOutlined />}
              onClick={() => {
                setIsShowAppCoin(true);
                setIdUser(String(record._id));
              }}
            />
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                setIsShowEdit(true);
                setDataEdit(record);
              }}
            />
            <Button
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
            {contextHolder}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
      const accessToken = Cookie.get("access_token");
      if (!accessToken) {
        setDataUser([]);
        return;
      }

      try {
        const users = await fetchAllUsers(accessToken);
        const sortedData = users.sort((a, b) =>
          String(b._id || b.username).localeCompare(String(a._id || a.username))
        );
        setDataUser(sortedData);
      } catch (err: any) {
        setDataUser([]);
        Swal.fire({
          icon: "error",
          title: "Không tải được danh sách user",
          text:
            err?.response?.data?.message ||
            "Vui lòng đăng nhập lại hoặc kiểm tra BE đã deploy bản mới.",
        });
      }
    };

    fetchData();
  }, [refreshTrigger]);

  const filteredUsers = useMemo(() => {
    return dataUser.filter((user: DataType) =>
      user.username.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, dataUser]);

  return (
    <div className="flex grid gap-5">
      <div className="flex justify-end">
        <Button
          type="primary"
          className="bg-blue-500 "
          onClick={() => setIsShowCreate(true)}
        >
          Create
        </Button>
      </div>
      <Input
        type="search"
        placeholder="Tìm theo tên người dùng..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      <Table<DataType>
        rowKey={(record) => record._id || record.username}
        columns={columns}
        dataSource={filteredUsers}
      />
      <ModalUser
        isShowCreate={isShowCreate}
        isShowEdit={isShowEdit}
        onCanEdit={() => setIsShowEdit(false)}
        onCancel={() => setIsShowCreate(false)}
        onRefesh={() => setRefreshTrigger((pev) => pev + 1)}
        data={dataEdit}
      />
      <ModalAppCoin
        id={idUser}
        isShowCoin={isShowAppCoin}
        onCanCoin={() => setIsShowAppCoin(false)}
        onRefesh={() => setRefreshTrigger((prev) => prev + 1)}
      />
    </div>
  );
};

export default ListUser;
